import $ from 'jquery';
import { defaultHooks } from '@wordpress/hooks';
import { select, dispatch } from '@wordpress/data';
import {
	isPayoneerGatewaySelected,
	submitOrderPayViaAjax,
	focusOnSelectedGateway
} from '../woocommerce';
import { PAYONEER_STORE_KEY } from '../../store';
import { setupClassicCheckoutListeners } from '../../store/storeListeners';
import OrderPlacementManager from '../OrderPlacementManager';

const log = ( ...args ) => console.log( '[payment page]', ...args );

const mountComponents = () => dispatch( PAYONEER_STORE_KEY ).mountComponents();
const payWithPayoneer = () => dispatch( PAYONEER_STORE_KEY ).pay();
const setOnPaymentUnsuccessfulNonce = () => dispatch( PAYONEER_STORE_KEY ).setOnPaymentUnsuccessfulNonce();
const releaseOnHoldOrder = ( requestData ) => dispatch( PAYONEER_STORE_KEY ).releaseOnHoldOrder( requestData );
const payoneerStore = select( PAYONEER_STORE_KEY );

export const initPaymentCheckout = () => {
	log( 'init' );

	const orderPlacementManager = new OrderPlacementManager( '#payment' );

	/**
	 * Set up hooks before everything else to ensure that we already capture
	 * actions that are fired during the following setup steps.
	 */

	defaultHooks.addAction(
		'payoneer.checkout.paymentMethodChanged',
		'payoneer/shortcodeCheckout',
		( { key, isValid, isReady } ) => {
			log( 'hook paymentMethodChanged' );

			if ( !key && !isPayoneerGatewaySelected() ) {
				// The method name is only set when a payoneer component is active.
				orderPlacementManager.setActive( false );
				return;
			}

			orderPlacementManager.setActive( true );
			orderPlacementManager.setProcessing( !isReady );

			// Apple Pay collects payment in its own sheet — gate on component readiness, not card validity.
			orderPlacementManager.setValidInput(
				payoneerStore.isApplePaySelected() ? !!isReady : isValid
			);
		}
	);
	const $cancelButton = $( '<button type="button" />' )
		.text( 'Cancel Payment' )
		.on( 'click', () => {
			// Hide the button and display the spinner.
			orderPlacementManager.setProcessingMessage( null );

			releaseOnHoldOrder( { longId: payoneerStore.getLongId() } )
				.then( () => window.location.reload() );
		} );

	const showCancelButton = ( paymentMethod ) => {
		if ( 'cards' === paymentMethod ) {
			return;
		}

		let testCancelButton = false;
		try {
			// Enable cancel button (unstable): localStorage.setItem( 'testCancelButton', true );
			testCancelButton = localStorage.getItem( 'testCancelButton' );
		} catch ( e ) {
		}

		if ( !testCancelButton ) {
			return;
		}

		// Replace the spinner with a cancel button, after a short delay.
		setTimeout( () => orderPlacementManager.setProcessingMessage( $cancelButton ), 2000 );
		// Hide the cancel button after 57 seconds, because the payment auto-completes after 60 seconds.
		setTimeout( () => orderPlacementManager.setProcessingMessage( null ), 57000 );
	};

	document.addEventListener(
		'DOMContentLoaded',
		() => {
			log( 'dom ready, setup listeners' );

			setupClassicCheckoutListeners();
			setOnPaymentUnsuccessfulNonce( PayoneerData.onPaymentUnsuccessfulNonce );

			const $orderReviewForm = $( '#order_review' );

			defaultHooks.addFilter(
				'payoneer.webSdk.onSdkError',
				'payoneer/paymentCheckout',
				( isResolved, checkout, component, data ) => {
					log( 'hook onSdkError', { isResolved, data } );

					const { payOrderErrorFlag } = window.PayoneerData;
					const url = new URL( document.location );

					url.searchParams.set( payOrderErrorFlag, true );
					window.location.href = url.toString();
					return isResolved;
				}
			);

			defaultHooks.addFilter(
				'payoneer.webSdk.onPaymentDeclined',
				'payoneer/paymentCheckout',
				async ( continueDefaultErrorHandling, sdk, componentName, responseData ) => {
					log( 'hook onPaymentDeclined' );
					handlePaymentUnsuccessful(responseData, 'declined');
					return false;
				}
			);

			defaultHooks.addFilter(
				'payoneer.webSdk.onPaymentFailure',
				'payoneer/paymentCheckout',
				async ( continueDefaultErrorHandling, sdk, componentName, responseData ) => {
					log( 'hook onPaymentFailure' );
					handlePaymentUnsuccessful(responseData, 'failure');
					return false;
				}
			);

			defaultHooks.addFilter(
				'payoneer.webSdk.onBeforeCharge',
				'payoneer/shortcodeCheckout',
				async () => {
					// Apple Pay: the buyer has authorized at the sheet — process the order + LIST now, then charge.
					if ( payoneerStore.isApplePaySelected() ) {
						try {
							const response = await submitOrderPayViaAjax( $orderReviewForm );
							const longId = response?.data?.longId;
							if ( !longId ) {
								handlePaymentUnsuccessful( response?.data ?? {}, 'failure' );
								return false;
							}
							dispatch( PAYONEER_STORE_KEY ).setLongIdToCharge( longId );
						} catch ( error ) {
							handlePaymentUnsuccessful( {}, 'failure' );
							return false;
						}
					}

					if ( payoneerStore.getLongId() !== payoneerStore.getLongIdToCharge() ) {
						handlePaymentUnsuccessful({}, 'list-mismatch');

						return false;
					}

					return true;
				}
			)

			const handlePaymentUnsuccessful = async function (responseData, paymentResult){
				dispatch( PAYONEER_STORE_KEY ).setLongIdToCharge( null );
				await releaseOnHoldOrder( {
					longId: payoneerStore.getLongId(),
					orderKey: new URLSearchParams( window.location.search ).get( 'key' ),
					errorTitleToDisplay: responseData?.interaction?.customerMessage?.title ?? 'Payment failed',
					errorTextToDisplay: responseData?.interaction?.customerMessage?.text ?? 'Please try again.',
					paymentResult
				} );

				window.location.reload();
			};

			/**
			 * The "pay for order" page does not trigger the "checkout_place_order" event.
			 */
			$orderReviewForm.on( 'submit', ( e ) => {
				log( 'event submit' );
				if ( !isPayoneerGatewaySelected() || payoneerStore.isHostedModeForced() ) {
					return;
				}

				e.preventDefault();
				e.stopImmediatePropagation();

				const { isValid, isReady, key } = payoneerStore.getActiveComponent();

				// Apple Pay: pay() must fire synchronously in this gesture; order processing runs in onBeforeCharge.
				if ( payoneerStore.isApplePaySelected() ) {
					if ( !isReady ) {
						try {
							focusOnSelectedGateway();
						} catch ( error ) {
							console.error( error );
						}
						return;
					}

					$orderReviewForm.block( {
						message: null,
						overlayCSS: { background: '#fff', opacity: 0.6 },
					} );
					payWithPayoneer();
					return;
				}

				if ( !isReady || !isValid ) {
					try {
						focusOnSelectedGateway();
					} catch ( error ) {
						console.error( error );
					}

					return;
				}

				// Block the entire order review form.
				$orderReviewForm.block( {
					message: null,
					overlayCSS: {
						background: '#fff',
						opacity: 0.6,
					},
				} );

				submitOrderPayViaAjax( $orderReviewForm )
					.then( (response) => {
						dispatch( PAYONEER_STORE_KEY ).setLongIdToCharge( response?.data?.longId );
						payWithPayoneer();
						showCancelButton( key );
					} )
					.catch( () => {
						/**
						 * Reloading enables us to see error messages added via wc_add_notice()
						 * Scrolling up makes sure the notices are in the viewport after the refresh
						 */
						window.scrollTo( 0, 0 );
						window.location.reload();
					} );
			} );

			mountComponents();
		},
		false
	);
};
